import {ApiService} from "../domin/api/ApiService";
import {User} from "../domin/pay/User";
import {PAYMENT_TYPE_PAYPAL} from "../const/Pay";
import {SKU_INFO} from "../const/LocalStorage";
import {Tracking} from "../tracking/tracking";

// Create a single Tracking instance to reuse
const tracking = new Tracking("en");

const checkResponse = async (response) => {
    if (!response) {
        return {};
    }
    if (!response?.ok) {
        let errorDetails = null;
        try {
            errorDetails = await response.json();
        } catch (e) {
            errorDetails = await response.text();
        }
        throw new Error(`Request failed: ${response.status} ${response.statusText}. Details: ${JSON.stringify(errorDetails)}`);
    }
    return response?.json() || {};
}

export const initialiseRequest = async () => {
    const user = new User();
    if (!user.getUserInfo()) {
        const apiServer = new ApiService();
        const response  = await fetch(apiServer.getCreateCustomerUserApiUrl(), {
            method: 'POST',
            headers: apiServer.getHeaders(),
            body: apiServer.getCreateCustomerRequestData()
        });
        const res = await checkResponse(response);
        if (res.result === 1 && res.response) {
            const userData = Object.assign({}, res.response.user, {
                userId: res.response?.user?.uuid,
                token: res.response?.access_token,
            });
            user.setUserInfo(userData);
            return user;
        }
    }
    return user;
}

export const createSubscriptionRequest = async (sku, email, discountAmount, dynamicCycleSubscription, paymentType = PAYMENT_TYPE_PAYPAL) => {
    const apiServer = new ApiService();
    const response  = await fetch(apiServer.getSubscriptionRequestDataUrl(), {
        method: 'POST',
        headers: apiServer.getHeaders(),
        body: apiServer.getSubscriptionRequestDataRequestData(sku, email, discountAmount, dynamicCycleSubscription, paymentType)
    });
    const res = await checkResponse(response);

    if (res.result === 1 && res.response) {
        return [res?.response?.session_id, res?.response?.client_secret, res?.response?.session_url];
    } else {
        return [null, null, null];
    }
}
export const verifyRequest = async (sessionId) => {
    const skuInfo = localStorage.getItem(SKU_INFO);
    if (!skuInfo) {
        // SKU info not found in localStorage
        tracking.logEventInstantly("verifyrequest_fail", {
            error: "sku info not found",
            string1: sessionId || ""
        }, "verify");
        return false;
    }
    
    const skuData = JSON.parse(skuInfo);
    const sku = skuData.sku;
    const paymentType = skuData.payment_type || PAYMENT_TYPE_PAYPAL;
    const skuSessionId = skuData.session_id;
    
    if (sessionId != null && sessionId !== skuSessionId) {
        // Session ID mismatch
        tracking.logEventInstantly("verifyrequest_fail", {
            error: "session id mismatch",
            string1: sessionId || "",
            string2: skuSessionId || "",
            sku: sku || ""
        }, "verify");
        return false;
    }

    try {
        const apiServer = new ApiService();
        const response = await fetch(apiServer.verifyRequestUrl(), {
            method: 'POST',
            headers: apiServer.getHeaders(),
            body: apiServer.verifyRequestData(sku, sessionId, paymentType)
        });
        
        const res = await checkResponse(response);
        
        if (!res || res.result != 1) {
            // API response failed
            tracking.logEventInstantly("verifyrequest_fail", {
                error: "api response failed",
                string1: sessionId || "",
                string2: res?.message || "no response",
                integer1: res?.result || 0,
                sku: sku || "",
                type: (paymentType || "") + ""
            }, "verify");
            return false;
        }
        
        const data = res.response.app_data;
        const result = JSON.parse(window.atob(data));
        const user = new User();
        const userInfo = user.getUserInfo();
        
        if (result.response && result.response.vip_info) {
            const vipInfo = result.response.vip_info;
            userInfo['vip'] = vipInfo.is_vip;
            userInfo['vipType'] = vipInfo.vip_type;
            user.setUserInfo(userInfo);
            return user;
        }
        
        // VIP info not found in response
        tracking.logEventInstantly("verifyrequest_fail", {
            error: "vip info not found",
            string1: sessionId || "",
            sku: sku || "",
            type: (paymentType || "") + ""
        }, "verify");
        return false;
        
    } catch (err) {
        // Exception during verification
        tracking.logEventInstantly("verifyrequest_fail", {
            error: err.message || "unknown error",
            string1: sessionId || "",
            string2: "exception",
            sku: skuData.sku || ""
        }, "verify");
        return false;
    }
}

export const uploadUserInfoRequest = async (email, userName) => {
    const apiServer = new ApiService();
    const response  = await fetch(apiServer.uploadUserInfoRequestUrl(), {
        method: 'POST',
        headers: apiServer.getHeaders(),
        body: apiServer.uploadUserInfoRequestData(email, userName)
    });
    const res = await checkResponse(response);
    if (!res || res.result != 1) {
        return false;
    }
    return true;
}

export const sendEmailForWebsiteRequest = async (questionnaireName, userId, formData) => {
    const apiServer = new ApiService();
    try {
        const response  = await fetch(apiServer.sendEmailForWebsiteUrl(), {
            method: 'POST',
            headers: apiServer.getHeaders(),
            body: apiServer.sendEmailForWebsiteRequestData(questionnaireName, userId, formData)
        });
        const res = await checkResponse(response);
        if (!res || res.result != 1) {
            return false;
        }
        const user = new User();
        user.setSinupEmail(formData.email);
    } catch (error) {
        console.error("Error in sendEmailForWebsiteRequest:", error);
        return false;
    }


    return true;
}
export const saveUserProfileRequest = async (userId, formData) => {
    const apiServer = new ApiService();
    let response ;
    try {
        response  = await fetch(apiServer.saveUserProfileUrl(), {
            method: 'POST',
            headers: apiServer.getHeaders(),
            body: apiServer.saveUserProfileData(userId, formData)
        });
    } catch (error) {
        console.error("Error in saveUserProfileRequest:", error);
        return false;
    }

    const res = await checkResponse(response);
    if (!res || res.result != 1) {
        return false;
    }
    return true;
}

export const loginOrCreateForWebsiteRequest = async (email, password) => {
    const apiServer = new ApiService();
    const response  = await fetch(apiServer.loginOrCreateForWebsiteUrl(), {
        method: 'POST',
        headers: apiServer.getHeaders(),
        body: apiServer.loginOrCreateForWebsiteData(email, password)
    });
    const res = await checkResponse(response);
    if (!res || res.result != 1) {

        alert(res?.message);
        return false;
    }
    if (res.response && res?.response?.user.user_id) {
        const user = new User();
        user.setSinupEmail(email);
    }
    return res;
}

export const getH5ResultRequest = async () => {
    const apiServer = new ApiService();
    const response  = await fetch(apiServer.getH5ResultApiUrl(), {
        method: 'GET',
        headers: apiServer.getHeaders()
    });
    const res = await checkResponse(response);
    if (!res || res.result != 1) {
        return false;
    }
    return res;
}